import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Mail, Download, UploadCloud, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { usePageMeta } from '../lib/usePageMeta';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_EMAIL = 'kariesha@thecrushmarketing.com';

/** Build a mailto: link with prefilled subject + body. */
function mailto(to, subject, body, cc) {
  const params = new URLSearchParams();
  if (subject) params.set('subject', subject);
  if (body) params.set('body', body);
  if (cc) params.set('cc', cc);
  return `mailto:${to}?${params.toString().replace(/\+/g, '%20')}`;
}

const STEP1_SUBJECT = 'Sending domain — [Your Company Name]';
const STEP1_BODY = `Hi Kariesha,

Here's the domain and email address we'd like our campaigns to send from.

Business name:
Domain (e.g. yourbusiness.com):
Send-from email (e.g. hello@yourbusiness.com):
Reply-to address, if different:

Thanks!`;

const STEP2_SUBJECT = 'Quick DNS setup — [Your Company Name]';
const STEP2_BODY = `Hi,

We're working with Crush Marketing to run our email campaigns, and they need a few DNS records added to our domain so our emails don't land in spam.

I've copied Kariesha here — she'll send you exactly what to add. It should take about 15 minutes, and nothing about our website changes.

Thanks!`;

export default function GetStarted() {
  usePageMeta({ title: 'Get started · Crush Marketing', robots: 'noindex, nofollow' });

  return (
    <main id="main" data-testid="get-started-page">
      {/* Header */}
      <section className="pt-10 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center fade-up">
          <p className="uppercase tracking-[0.16em] text-[11px] sm:text-xs font-semibold mb-4 sm:mb-5" style={{ color: '#5132d4' }}>
            Get started
          </p>
          <h1 className="font-serif text-[2rem] leading-[1.1] sm:text-[2.5rem] md:text-5xl lg:text-[3rem] tracking-tight" style={{ color: '#130F28' }}>
            We're glad <em className="italic" style={{ color: '#5132d4' }}>you're here.</em>
          </h1>
          <p className="mt-5 sm:mt-6 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: '#3b3656' }}>
            Three things and we're ready to go. Send them whenever you have them, you don't have to do it all at once.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="pb-16 sm:pb-20 lg:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 sm:space-y-7">
          <Step1 />
          <Step2 />
          <Step3 />
        </div>
      </section>
    </main>
  );
}

/* --------------------------------- helpers -------------------------------- */

function StepCard({ n, title, subtitle, children }) {
  return (
    <article
      data-testid={`get-started-step-${n}`}
      className="rounded-2xl bg-white border p-6 sm:p-8 shadow-[0_18px_40px_-24px_rgba(19,15,40,0.22)]"
      style={{ borderColor: '#e6e2d6' }}
    >
      <div className="flex items-start gap-4 sm:gap-5">
        <span
          aria-hidden="true"
          className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center font-serif text-lg font-semibold text-white"
          style={{ backgroundColor: '#6B4BEF' }}
        >
          {n}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-2xl sm:text-3xl leading-tight" style={{ color: '#130F28' }}>{title}</h2>
          <p className="mt-3 text-base leading-relaxed" style={{ color: '#3b3656' }}>{subtitle}</p>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </article>
  );
}

function MailToButton({ href, label, testId }) {
  return (
    <a
      href={href}
      data-testid={testId}
      className="btn-primary inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm sm:text-base font-semibold w-full sm:w-auto"
    >
      <Mail size={16} aria-hidden="true" />
      {label}
    </a>
  );
}

function EmailAddress({ address, note }) {
  return (
    <p className="mt-3 text-sm" style={{ color: '#3b3656' }}>
      {note ? <span className="block mb-1">{note}</span> : null}
      <span className="font-mono text-[13px] px-2.5 py-1.5 rounded-md border select-all" style={{ borderColor: '#e6e2d6', backgroundColor: '#faf8f2', color: '#130F28' }}>
        {address}
      </span>
    </p>
  );
}

/* ---------------------------------- Step 1 -------------------------------- */

function Step1() {
  return (
    <StepCard
      n={1}
      title="Your sending domain"
      subtitle="This is the address your customers will see. It should be a real inbox someone monitors, so replies don't disappear."
    >
      <MailToButton
        testId="step1-mail-button"
        href={mailto(CONTACT_EMAIL, STEP1_SUBJECT, STEP1_BODY)}
        label="Send us your domain"
      />
      <EmailAddress address={CONTACT_EMAIL} />
    </StepCard>
  );
}

/* ---------------------------------- Step 2 -------------------------------- */

function Step2() {
  return (
    <StepCard
      n={2}
      title="A quick DNS setup"
      subtitle={
        <>
          Right now, your domain doesn't know who we are. So if we send an email that says it's from you, Gmail and Outlook have no way to tell whether it's really you or someone pretending to be you, and they'll usually assume the worst and send it to spam.
          <br /><br />
          DNS records are how your domain vouches for us. It's a short list of settings your IT or hosting person adds one time, and it tells email providers that we're allowed to send on your behalf. Takes about 15 minutes, and we'll send them exactly what to add.
          <br /><br />
          Nothing about your website changes. Your email keeps working the way it does today.
        </>
      }
    >
      <MailToButton
        testId="step2-mail-button"
        // "To" left empty so the customer fills in their IT person; Kariesha is CC'd
        href={mailto('', STEP2_SUBJECT, STEP2_BODY, CONTACT_EMAIL)}
        label="Email your IT person"
      />
      <EmailAddress
        address={CONTACT_EMAIL}
        note={<>Manage your own DNS? Email <span className="font-semibold">kariesha@thecrushmarketing.com</span> and we'll send you the records directly.</>}
      />
    </StepCard>
  );
}

/* ---------------------------------- Step 3 -------------------------------- */

const SAMPLE_ROWS = [
  ['Sarah', 'Johnson', 'sarah@example.com', 'Acme Corp', '(843) 555-0100', '2024-11-05', 'Current Client'],
  ['Michael', 'Chen', 'michael@brightstudios.co', 'Bright Studios', '(843) 555-0134', '2023-06-18', 'Past Client'],
];
const COLUMNS = ['First Name', 'Last Name', 'Email', 'Company', 'Phone', 'Last Service Date', 'Relationship'];

function Step3() {
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'xlsx'].includes(ext || '')) {
      setErrorMsg('Please choose a .csv or .xlsx file.');
      setFile(null);
      e.target.value = '';
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setErrorMsg('File is too large (5 MB max).');
      setFile(null);
      e.target.value = '';
      return;
    }
    setErrorMsg('');
    setFile(f);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setErrorMsg('');
    if (!businessName.trim() || !email.trim() || !file) {
      setErrorMsg('All fields are required.');
      return;
    }
    setStatus('submitting');
    const form = new FormData();
    form.append('business_name', businessName.trim());
    form.append('email', email.trim());
    form.append('file', file);
    try {
      await axios.post(`${API_BASE}/onboarding/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStatus('done');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Per spec — soft-fail is still success from user's perspective if the
      // failure was internal (e.g. notification). Only hard-fail on 4xx validation.
      const status_code = err?.response?.status;
      if (status_code && status_code >= 400 && status_code < 500) {
        setErrorMsg(typeof detail === 'string' ? detail : 'Upload failed. Please double-check the file and try again.');
        setStatus('error');
      } else {
        // Treat 5xx / network as a soft-success so the user isn't blocked.
        setStatus('done');
      }
    }
  };

  return (
    <StepCard
      n={3}
      title="Your client list"
      subtitle="This is who we'll be talking to. The more complete it is, the better your campaigns perform. A partial list is fine to start, you can send more later."
    >
      <a
        href="/customer-list-template.csv"
        download
        data-testid="template-download"
        className="btn-primary inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm sm:text-base font-semibold w-full sm:w-auto"
      >
        <Download size={16} aria-hidden="true" />
        Download CSV template
      </a>

      <div className="mt-6 rounded-xl border overflow-hidden" style={{ borderColor: '#e6e2d6' }}>
        <div className="px-4 py-2 text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ backgroundColor: '#faf8f2', color: '#4a4566', borderBottom: '1px solid #e6e2d6' }}>
          Example rows
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr style={{ backgroundColor: '#fbfaf6' }}>
                {COLUMNS.map((c) => (
                  <th key={c} className="text-left px-3 py-2 font-semibold whitespace-nowrap" style={{ color: '#130F28' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SAMPLE_ROWS.map((row, i) => (
                <tr key={i} className={i % 2 ? '' : ''} style={{ borderTop: '1px solid #efece2', color: '#3b3656' }}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-3 py-2 whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t" style={{ borderColor: '#efece2' }}>
        <p className="text-sm leading-relaxed" style={{ color: '#3b3656' }}>
          Please upload your list here rather than emailing it. Customer lists contain personal information, and this keeps it encrypted and stored securely.
        </p>

        {status === 'done' ? (
          <div
            data-testid="upload-success"
            className="mt-5 p-5 rounded-xl fade-up"
            style={{ backgroundColor: 'rgba(16,201,143,0.10)', border: '1px solid rgba(16,201,143,0.35)' }}
          >
            <p className="flex items-start gap-2 text-sm sm:text-base font-semibold" style={{ color: '#0a5c43' }}>
              <CheckCircle2 size={18} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
              Got it. We'll be in touch within one business day.
            </p>
          </div>
        ) : (
          <form className="mt-5 space-y-4" onSubmit={submit} noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-semibold" style={{ color: '#4a4566' }}>Business name <span style={{ color: '#a31a1a' }}>*</span></span>
                <input
                  type="text"
                  required
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  data-testid="upload-business"
                  className="field-input mt-1"
                  placeholder="Apex HVAC & Plumbing"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold" style={{ color: '#4a4566' }}>Your email <span style={{ color: '#a31a1a' }}>*</span></span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="upload-email"
                  className="field-input mt-1"
                  placeholder="sarah@apexhvac.com"
                />
              </label>
            </div>
            <div>
              <span className="text-xs font-semibold block mb-1" style={{ color: '#4a4566' }}>Customer list file <span style={{ color: '#a31a1a' }}>*</span></span>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full text-left rounded-xl border-2 border-dashed p-5 transition focus:outline-none focus:ring-2 focus:ring-[#6B4BEF]"
                style={{
                  borderColor: file ? '#10C98F' : '#bdb8a3',
                  backgroundColor: file ? 'rgba(16,201,143,0.08)' : '#fbfaf6',
                }}
              >
                {file ? (
                  <p className="text-sm font-semibold flex items-center gap-2" style={{ color: '#0a5c43' }}>
                    <CheckCircle2 size={18} aria-hidden="true" /> {file.name}
                    <span className="ml-auto text-xs font-normal" style={{ color: '#3b3656' }}>Click to change</span>
                  </p>
                ) : (
                  <div className="flex items-center gap-3">
                    <span aria-hidden="true" className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#f0ece0' }}>
                      <UploadCloud size={18} style={{ color: '#5132d4' }} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: '#130F28' }}>Click to choose a file</p>
                      <p className="text-xs" style={{ color: '#4a4566' }}>.CSV or .XLSX &middot; 5 MB max</p>
                    </div>
                  </div>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={onFile}
                data-testid="upload-file"
                className="sr-only"
              />
            </div>

            {errorMsg && (
              <p role="alert" data-testid="upload-error" className="text-sm flex items-center gap-2" style={{ color: '#a31a1a' }}>
                <AlertCircle size={16} aria-hidden="true" /> {errorMsg}
              </p>
            )}

            <button
              type="submit"
              data-testid="upload-submit"
              disabled={status === 'submitting'}
              className="btn-primary rounded-full px-6 py-3 text-sm sm:text-base font-semibold w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Uploading…
                </>
              ) : (
                <>Upload securely</>
              )}
            </button>
          </form>
        )}
      </div>
    </StepCard>
  );
}
